import { Virtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useRef } from "react";
import { VisibleNode } from "~/components/build/Show/components/Sidebar/useTreeNodes";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

export function useScrollToId(
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  nodes: VisibleNode[],
) {
  const { currentLatestJob, currentStep } = useCurrentJobOrStep();
  const params = useQueryParams();
  const scrollIndexRef = useRef<number>(undefined);

  /**
   * Scroll element into view if it's not currently visible.
   */
  const scrollToId = useCallback(
    (id: string) => {
      // If we've already scrolled to an index, don't scroll again
      if (scrollIndexRef.current !== undefined) {
        return;
      }

      if (!id || !rowVirtualizer.scrollRect || !rowVirtualizer.scrollElement) {
        return;
      }

      const virtualItem = rowVirtualizer.getVirtualItems().find((item) => item.key === id);
      const scrollTop = rowVirtualizer.scrollElement.scrollTop;
      const containerHeight = rowVirtualizer.scrollRect.height;

      if (
        !virtualItem ||
        virtualItem.start < scrollTop ||
        virtualItem.end > scrollTop + containerHeight
      ) {
        const index = nodes.findIndex((node) => node.id === id);
        if (index > -1) {
          rowVirtualizer.scrollToIndex(index, { align: "start" });
          scrollIndexRef.current = index;
        }
      } else {
        scrollIndexRef.current = virtualItem.index;
      }
    },
    [rowVirtualizer, nodes.length],
  );

  useEffect(() => {
    // Reset the scroll index ref when the job or step changes
    scrollIndexRef.current = undefined;
  }, [params.jobUuid, params.stepUuid]);

  useEffect(() => {
    if (currentLatestJob?.id) {
      scrollToId(currentLatestJob.id);
    } else if (currentStep?.uuid) {
      scrollToId(currentStep.uuid);
    }
  }, [currentLatestJob, currentStep, scrollToId]);
}
